import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { getHeaders/*, handleErrorResponse, handleResponse*/ } from '../../lib/requests';
import { useNavigate } from 'react-router-dom';
import MasterLayout from '../../components/layouts/masterlayout';
import ErrorMessage from '../../components/common/errormessage';
import SuccessMessage from '../../components/common/successmessage';
import GeneralTextBox from '../common/textboxes/textbox_general';
import PhoneTextBox from '../common/textboxes/textbox_phone';
import GeneralButton from '../common/button_general';
// import GeneralDropdown from '../common/dropdowns/dropdown_general';
import CheckboxGroup from '../common/checkbox_group';
import Spinner from '../common/spinner';
import ProviderDropdown from '../common/dropdowns/dropdown_providers';

const UserForm = () => {
    const navigate = useNavigate();
    const { userId } = useParams();
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        name: '',
        phone_number: '',
        client_id: null,
        groups: {
            Admin: false,
            Provider: false,
            Client: false,
            Customer: false,
        }
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleCheckboxChange = (value) => {
        setFormData(prevState => ({
            ...prevState,
            groups: {
                ...prevState.groups,
                [value]: !prevState.groups[value]
            }
        }));
    };

    const handleProviderSelect = (provider) => {
        setFormData(prevState => ({
            ...prevState,
            client_id: provider ? provider.client_id : null
        }));
    };

    const checkboxOptions = [
        { id: 'admin-checkbox', value: 'Admin', label: 'Admin', checked: formData.groups.Admin },
        { id: 'provider-checkbox', value: 'Client', label: 'Provider', checked: formData.groups.Client },
        { id: 'client-checkbox', value: 'Customer', label: 'Client', checked: formData.groups.Customer },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;

        try {
            const method = userId ? 'put' : 'post';

             const transformedFormData = {
                ...formData,
                groups: { ...formData.groups },
                ...(userId
                    ? {}                              // update ­‑ keep `password` if supplied
                    : { temporaryPassword: formData.password }  // create ­‑ Cognito expects this
                )
            };
            if (!userId || !transformedFormData.password) delete transformedFormData.password;

            await axios({
                method,
                url: `${apiUrl}/users${userId ? `/${userId}` : ''}`,
                data: transformedFormData,
                headers: getHeaders(apiToken),
            });

            setShowSuccess(true);
            setSuccessMessage(userId ? 'User updated successfully.' : 'User registration successful. Please check your email for verification.');
            setIsLoading(false);
            if (!userId) {
                navigate('/users');
            }
        } catch (error) {
            setShowError(true);
            setErrorMessage(error.message || 'An error occurred during registration.');
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!userId) return;

        const fetchUserData = async () => {
            setIsLoading(true);
            try {
                const apiToken = localStorage.getItem('accessToken');
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
                    headers: getHeaders(apiToken)
                });
                const userData = response.data.data;

                const groupObject = {
                    Admin: false,
                    Provider: false,
                    Client: false,
                };

                userData.Groups.forEach(group => {
                    if (group in groupObject) {
                        groupObject[group] = true;
                    }
                });

                setFormData({
                    username: userData.Username || '',
                    email: userData.UserAttributes.find(attr => attr.Name === 'email')?.Value || '',
                    name: userData.UserAttributes.find(attr => attr.Name === 'name')?.Value || '',
                    phone_number: userData.UserAttributes.find(attr => attr.Name === 'phone_number')?.Value || '',
                    client_id: userData.UserAttributes.find(attr => attr.Name === 'custom:client_id')?.Value || null,
                    groups: groupObject || {
                        Admin: false,
                        Provider: false,
                        Client: false,
                        Customer: false,
                    },
                    password: ''  // Passwords are typically not fetched for security reasons
                });
                setShowError(false);
            } catch (error) {
                setShowError(true);
                setErrorMessage(error.message || 'Failed to fetch user details.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserData();
    }, [userId]);

    const pageTitle = `${userId ? 'Edit User ' + userId : 'Create User'}`;

    return (
        <MasterLayout pageTitle={pageTitle}>
            {showError && <ErrorMessage message={errorMessage} />}
            {showSuccess && <SuccessMessage message={successMessage} setShowSuccess={setShowSuccess} />}
            {isLoading ? <Spinner /> : (
                <form onSubmit={handleSubmit} className="p-4">
                    <GeneralTextBox
                        label="Name" id="name" name="name"
                        value={formData.name} onChange={handleChange}
                        placeholder="Name" required={true}
                    />
                    <GeneralTextBox
                        label="Email" id="email" name="email"
                        value={formData.email} onChange={handleChange}
                        placeholder="Email" required={true}
                    />
                    <GeneralTextBox
                        label="Username" id="username" name="username"
                        value={formData.username} onChange={handleChange}
                        placeholder="Username" required={true}
                        autoComplete="off"
                    />
                    <PhoneTextBox
                        label="Phone Number" id="phone_number" name="phone_number"
                        value={formData.phone_number} onChange={handleChange}
                        placeholder="Phone Number" required={true}
                    />
                    <GeneralTextBox
                        label="Password" id="password" name="password" type="password"
                        value={formData.password} onChange={handleChange}
                        placeholder="Password"
                        autoComplete="new-password"
                    />
                    <CheckboxGroup
                        title="User Group"
                        id="group"
                        name="group"
                        onChange={handleCheckboxChange}
                        options={checkboxOptions}
                        placeholder="Select a Group"
                        layout='horizontal'
                    />
                    {(formData.groups.Client || formData.groups.Customer) && (
                        <div className="mb-4">
                            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                Associated Provider
                            </label>
                            <ProviderDropdown
                                onSelect={handleProviderSelect}
                                selectedProviderId={formData.client_id}
                            />
                        </div>
                    )}
                    <div className="flex justify-center mt-6">
                        <GeneralButton type="submit" buttonType="primary">{userId ? 'Update User' : 'Register User'}</GeneralButton>
                    </div>
                </form>
            )}
        </MasterLayout>
    );
};

export default UserForm;
